// Force rebuild: 2025-07-01T17:36:00Z
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import Assessments from './pages/Assessments';
import ResultsPage from './pages/ResultsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import StudentsPage from './pages/StudentsPage';
import StudentEditPage from './pages/StudentEditPage';
import CreateAssessmentPage from './pages/CreateAssessmentPage';
import ReportsPage from './pages/ReportsPage';
import UserManagementPage from './pages/UserManagementPage';
import SettingsPage from './pages/SettingsPage';
import ViewAssessment from './pages/ViewAssessment';
import ProfilePage from './pages/ProfilePage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import PendingPatientsPage from './pages/PendingPatientsPage';
import TeamsPage from './pages/TeamsPage';
import ProviderResultsPage from './pages/ProviderResultsPage';
import TeamDetailsPage from './pages/TeamDetailsPage';
import ViewOrganizationsPage from './pages/ViewOrganizationsPage';
import ManageSurveysPage from './pages/ManageSurveysPage';
import EditSurveyPage from './pages/EditSurveyPage';
import ViewAssignedSurveysPage from './pages/ViewAssignedSurveysPage';
import ProviderPatientResultPage from './pages/ProviderPatientResultPage';
import FollowUpsListPage from './pages/FollowUpsListPage';
import UniversitiesPage from './pages/UniversitiesPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <ToastContainer position="top-right" autoClose={5000} />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Student Routes */}
            <Route 
              path="/assessments" 
              element={
                <ProtectedRoute>
                  <Assessments />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assessments/:id" 
              element={
                <ProtectedRoute>
                  <ViewAssessment />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/results" 
              element={
                <ProtectedRoute>
                  <ResultsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/appointments" 
              element={
                <ProtectedRoute>
                  <AppointmentsPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Provider Routes */}
            <Route 
              path="/students" 
              element={
                <ProtectedRoute>
                  <StudentsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/students/:id/edit" 
              element={
                <ProtectedRoute>
                  <StudentEditPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/pending-patients" 
              element={
                <ProtectedRoute>
                  <PendingPatientsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/provider/results" 
              element={
                <ProtectedRoute>
                  <ProviderResultsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teams" 
              element={
                <ProtectedRoute>
                  <TeamsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/teams/:id" 
              element={
                <ProtectedRoute>
                  <TeamDetailsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/organizations" 
              element={
                <ProtectedRoute>
                  <ViewOrganizationsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/create-assessment" 
              element={
                <ProtectedRoute>
                  <CreateAssessmentPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/manage-surveys" 
              element={
                <ProtectedRoute>
                  <ManageSurveysPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/edit-survey/:id" 
              element={
                <ProtectedRoute>
                  <EditSurveyPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/assigned-surveys" 
              element={
                <ProtectedRoute>
                  <ViewAssignedSurveysPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/provider/patients/:patientId/assessments/:assessmentId/results" 
              element={
                <ProtectedRoute>
                  <ProviderPatientResultPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/followups" 
              element={
                <ProtectedRoute>
                  <FollowUpsListPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="/reports" 
              element={
                <ProtectedRoute>
                  <ReportsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/user-management" 
              element={
                <ProtectedRoute>
                  <UserManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/universities" 
              element={
                <ProtectedRoute>
                  <UniversitiesPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Common Routes for all authenticated users */}
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/change-password" 
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              } 
            />
            
            <Route path="/" element={<Navigate to="/dashboard" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;